import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { DocumentService, CustomsClearanceProgressRequest, CustomsClearanceResult } from '../../services/document.service';

// JWT Debug interfaces
interface JwtDebugInfo {
  Token: string;
  DecodedInfo: {
    Header: string;
    Payload: string;
    Account: string;
    SecretKeyMasked: string;
    IssuedAt: string;
    ExpiresAt: string;
  };
  CredentialsUsed: {
    Account: string;
    SecretMasked: string;
    ClientName: string;
    EndpointUsed: string;
  };
}

interface CustomsClearanceResponseWithDebug {
  success: boolean;
  data: {
    SuccessCount: number;
    FailedCount: number;
    Results: any[];
    ClientIntegrationApiResponse?: {
      success: boolean;
      httpStatusCode: number;
      clientType: string;
      systemFeature: string;
      resolvedEndpointUrl: string;
      responseData?: string;  // Raw response from Shopee API
      errorMessage: string | null;
      callTimeUtc: string;
    };
    DebugInfo?: {
      ActualUrl: string;
      Endpoint: string;
      Account: string;
      Secret: string;
      ApiBaseUrl: string;
      ClientId: string;
      ClientName: string;
      HasValidCredentials: boolean;
      Note: string;
      JwtDebugInfo?: JwtDebugInfo;
    };
  };
  message: string;
}

@Component({
  selector: 'app-customs-clearance-progress',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './customs-clearance-progress.component.html',
  styleUrls: ['./customs-clearance-progress.component.css']
})
export class CustomsClearanceProgressComponent implements OnInit, OnDestroy {
  isLoading = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  ccResults: CustomsClearanceResult[] = [];
  
  // Form for adding new results
  newResult: CustomsClearanceResult = this.createEmptyResult();

  // JWT Debug properties
  jwtDebugInfo: JwtDebugInfo | null = null;
  debugInfoVisible = false;
  autoCloseTimer: any = null;

  constructor(
    private http: HttpClient,
    private documentService: DocumentService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.clearMessages();

    // Check for navigation state (parcels from carton detail)
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state || (typeof history !== 'undefined' ? history.state : null);

    if (state && state.trackingNumbers && state.source === 'carton-detail') {
      // Pre-populate with tracking numbers from carton
      const trackingNumbers: string[] = state.trackingNumbers;

      if (trackingNumbers && trackingNumbers.length > 0) {
        this.ccResults = [];
        trackingNumbers.forEach(trackingNo => {
          const result = this.createEmptyResult();
          result.reference_no = trackingNo;
          this.ccResults.push(result);
        });

        this.successMessage = `Pre-populated ${trackingNumbers.length} parcel(s) from carton ${state.cartonNo || ''}. You can add/remove parcels or modify fields as needed.`;
      } else {
        this.addEmptyResult();
      }
    } else {
      // Normal mode - start with one empty result
      this.addEmptyResult();
    }
  }

  createEmptyResult(): CustomsClearanceResult {
    return {
      reference_no: '',
      clear_type: 1, // Default to Import
      op_time: Math.floor(Date.now() / 1000), // Current timestamp
      op_location: '',
      event_code: '', // User must select from dropdown
      
      // Optional fields - backend still expects them for now
      operator: '',
      img_url: '',
      tax_base_type: 1,
      taxable_amount: 0,
      duty_rate: 0,
      duty_amount: 0,
      vat_rate: 0,
      vat_amount: 0,
      customs_clearance_fee: 0,
      tax_proof: '',
      reason: '',
      remark: '',
      partner_cte_access_key: '',
      dir_number: ''
    };
  }

  clearMessages(): void {
    this.errorMessage = null;
    this.successMessage = null;
  }

  addEmptyResult(): void {
    this.ccResults.push(this.createEmptyResult());
  }

  removeResult(index: number): void {
    if (this.ccResults.length > 1) {
      this.ccResults.splice(index, 1);
    }
  }

  submitCustomsClearanceProgress(): void {
    this.clearMessages();
    
    // Validate required fields - only the 5 mandatory fields
    const invalidResults = this.ccResults.filter(result => 
      !result.reference_no.trim() || 
      !result.clear_type ||
      !result.op_location.trim() || 
      !result.event_code.trim()
    );

    if (invalidResults.length > 0) {
      this.errorMessage = 'Please fill in all required fields: Reference No, Clear Type, Operation Location, and Event Code.';
      return;
    }

    this.isLoading = true;

    const request: CustomsClearanceProgressRequest = {
      cc_results: this.ccResults
    };

    this.documentService.submitCustomsClearanceProgress(request).subscribe({
      next: (response: CustomsClearanceResponseWithDebug) => {
        console.log('Customs clearance progress response:', response);

        // Log raw Shopee API response for debugging
        if (response.data?.ClientIntegrationApiResponse?.responseData) {
          console.log('🔍 Raw Shopee API Response:', response.data.ClientIntegrationApiResponse.responseData);
          try {
            const parsedResponse = JSON.parse(response.data.ClientIntegrationApiResponse.responseData);
            console.log('📦 Parsed Shopee API Response:', parsedResponse);
          } catch (e) {
            console.log('⚠️ responseData is not JSON:', response.data.ClientIntegrationApiResponse.responseData);
          }
        }

        if (response.success) {
          this.successMessage = `Customs clearance progress processed successfully. ${response.data?.SuccessCount || 0} successful, ${response.data?.FailedCount || 0} failed.`;

          // Handle JWT debug info if present (nested under DebugInfo)
          if (response.data?.DebugInfo?.JwtDebugInfo) {
            this.handleJwtDebugInfo(response.data.DebugInfo.JwtDebugInfo);
          }

          this.resetForm();
        } else {
          this.errorMessage = response.message || 'Failed to process customs clearance progress';
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Customs clearance progress error:', error);
        this.errorMessage = error.error?.message || error.message || 'Failed to process customs clearance progress. Please try again.';
        this.isLoading = false;
      }
    });
  }

  resetForm(): void {
    this.ccResults = [];
    this.clearMessages();
    this.addEmptyResult(); // Start with one empty result
  }

  // Format timestamp for display
  formatTimestamp(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleString();
  }

  // Update timestamp when date/time changes
  updateTimestamp(index: number, dateTimeValue: string): void {
    if (dateTimeValue) {
      this.ccResults[index].op_time = Math.floor(new Date(dateTimeValue).getTime() / 1000);
    }
  }

  onOpTimeChange(index: number, e: Event): void {
    this.updateTimestamp(index, (e.target as HTMLInputElement).value);
  }

  // Get current datetime value for input
  getDateTimeValue(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    return date.toISOString().slice(0, 16); // Format for datetime-local input
  }

  // JWT Debug methods
  handleJwtDebugInfo(debugInfo: JwtDebugInfo): void {
    this.jwtDebugInfo = debugInfo;
    this.debugInfoVisible = true;

    // Auto-clear after 30 seconds for security
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
    }

    this.autoCloseTimer = setTimeout(() => {
      this.clearDebugInfo();
    }, 30000);
  }

  clearDebugInfo(): void {
    this.jwtDebugInfo = null;
    this.debugInfoVisible = false;
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      // Show temporary success message
      console.log('JWT token copied to clipboard');
      // Could add a toast notification here in the future
    }).catch(err => {
      console.error('Failed to copy JWT token: ', err);
    });
  }

  ngOnDestroy(): void {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
    }
  }
}




